import { BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { NcFileBatchEventsLink, NcFileBatchEventType } from '@v2/pages/common/enums';
import { createLevelSubScreenLinking } from '@v2/pages/common/helpers';
import { JobDetailsScreen, JobDetailsScreenLink, JobLevelSubScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { injectConditionalArrayItems } from '@helpers';
import { JobDetailsJobs, JobDetailsJobsDocuments, JobDetailsJobsTimeline } from '@v2/pages/job/modules/jobs';
import {
  JobDetailsNCFilePerformanceActive,
  JobDetailsNCFilePerformanceNavigator,
  JobDetailsNCFilePerformancePaid,
  JobDetailsNCFilePerformanceProductive,
  JobDetailsNCFilePerformanceUtilization,
  JobDetailsNCFile,
  JobDetailsNCFileDocuments,
  JobDetailsNCFileEventsActive,
  JobDetailsNCFileEventsChange,
  JobDetailsNCFileEventsCycle,
  JobDetailsNCFileEventsNavigator,
  JobDetailsNCFileEventsSetup,
  JobDetailsNCFileEventsTrace,
  JobDetailsNCFileLinks,
  JobDetailsNCFileTimeline,
  JobDetailsNCFileVersions,
  JobDetailsNCFileTraceability,
} from '../modules/nc-file';
import {
  JobDetailsBatchEventsActive,
  JobDetailsBatchEventsChange,
  JobDetailsBatchEventsCycle,
  JobDetailsBatchEventsNavigator,
  JobDetailsBatchEventsSetup,
  JobDetailsBatchEventsTrace,
  JobDetailsBatch,
  JobDetailsBatchDocuments,
  JobDetailsBatchTimeline,
} from '../modules/batch';
import { JobDetailsPart, JobDetailsPartDocuments, JobDetailsPartTimeline } from '@v2/pages/job/modules/part';
import { JobDetailsTools } from '@v2/pages/job/modules/tools/JobDetailsTools';
import { JobDetailsBatchNavigator } from './JobDetailsBatchNavigator';
import { JobDetailsJobNavigator } from './JobDetailsJobNavigator';
import { JobDetailsNCFileNavigator } from './JobDetailsNCFileNavigator';
import { JobDetailsPartNavigator } from './JobDetailsPartNavigator';
import { JobDetailsToolNavigator } from './JobDetailsToolNavigator';
import { timelineAvailable } from '@config/navigationRestrictions';
import {
  filteredBatchLevelSubScreen,
  filteredBatchLevelSubScreenLink,
  filteredJobLevelSubScreen,
  filteredJobLevelSubScreenLink,
  filteredNCFileLevelSubScreen,
  filteredNCFileLevelSubScreenLink,
  filteredPartLevelSubScreen,
  filteredPartLevelSubScreenLink,
} from '../../common/constants/base.constants';
import { ToolLevelSubScreen, ToolLevelSubScreenLink } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { PartLevelSubScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { MachinesScreen } from '@v2/pages/machines/common/enums';

export const JobDetailsScreenName = 'JobDetailsV2';

export const jobDetailsLinking = {
  path: 'job/details',
  screens: {
    [JobDetailsScreen.PART]: {
      path: JobDetailsScreenLink.PART,
      screens: createLevelSubScreenLinking(JobDetailsScreen.PART, {
        levels: filteredPartLevelSubScreen,
        links: filteredPartLevelSubScreenLink,
      }),
    },
    [JobDetailsScreen.BATCH]: {
      path: JobDetailsScreenLink.BATCH,
      screens: createLevelSubScreenLinking(JobDetailsScreen.BATCH, {
        levels: filteredBatchLevelSubScreen,
        links: filteredBatchLevelSubScreenLink,
        nestedScreens: {
          [BatchLevelSubScreen.TIMES]: {
            screenName: BatchLevelSubScreen.TIMES,
            items: NcFileBatchEventType,
            links: NcFileBatchEventsLink,
          },
        },
      }),
    },
    [JobDetailsScreen.NC_FILE]: {
      path: JobDetailsScreenLink.NC_FILE,
      screens: createLevelSubScreenLinking(JobDetailsScreen.NC_FILE, {
        levels: filteredNCFileLevelSubScreen,
        links: filteredNCFileLevelSubScreenLink,
        nestedScreens: {
          [NCFileLevelSubScreen.TIMES]: {
            screenName: NCFileLevelSubScreen.TIMES,
            items: NcFileBatchEventType,
            links: NcFileBatchEventsLink,
          },
        },
      }),
    },
    [JobDetailsScreen.JOBS]: {
      path: JobDetailsScreenLink.JOBS,
      screens: createLevelSubScreenLinking(JobDetailsScreen.JOBS, {
        levels: filteredJobLevelSubScreen,
        links: filteredJobLevelSubScreenLink,
      }),
    },
    [JobDetailsScreen.TOOLS]: {
      path: JobDetailsScreenLink.TOOLS,
      screens: createLevelSubScreenLinking(JobDetailsScreen.TOOLS, {
        levels: ToolLevelSubScreen,
        links: ToolLevelSubScreenLink,
      }),
    },
  },
};

export const jobsDetailsConfig = [
  { name: JobDetailsScreen.PART, Component: JobDetailsPartNavigator },
  { name: JobDetailsScreen.BATCH, Component: JobDetailsBatchNavigator },
  { name: JobDetailsScreen.NC_FILE, Component: JobDetailsNCFileNavigator },
  { name: JobDetailsScreen.JOBS, Component: JobDetailsJobNavigator },
  { name: JobDetailsScreen.TOOLS, Component: JobDetailsToolNavigator },
];

export const jobDetailsPartConfig = [
  {
    name: `${JobDetailsScreen.PART}${PartLevelSubScreen.GENERAL}`,
    Component: JobDetailsPart,
  },
  ...injectConditionalArrayItems(timelineAvailable, {
    name: `${JobDetailsScreen.PART}${PartLevelSubScreen.TIMELINE}`,
    Component: JobDetailsPartTimeline,
  }),
  // {
  //   name: `${JobDetailsScreen.PART}${PartLevelSubScreen.TIMES}`,
  //   Component: JobDetailsPart,
  // },
  // {
  //   name: `${JobDetailsScreen.PART}${PartLevelSubScreen.PERFORMANCE}`,
  //   Component: JobDetailsPartPerformanceNavigator,
  // },
  // {
  //   name: `${JobDetailsScreen.PART}${PartLevelSubScreen.VERSIONS}`,
  //   Component: JobDetailsPart,
  // },
  // {
  //   name: `${JobDetailsScreen.PART}${PartLevelSubScreen.LINKS}`,
  //   Component: JobDetailsPart,
  // },
  {
    name: `${JobDetailsScreen.PART}${PartLevelSubScreen.DOCUMENTS}`,
    Component: JobDetailsPartDocuments,
  },
];

export const jobDetailsToolConfig = [
  {
    name: `${JobDetailsScreen.TOOLS}${ToolLevelSubScreen.GENERAL}`,
    Component: JobDetailsTools,
  },
];

export const jobDetailsBatchConfig = [
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.GENERAL}`,
    Component: JobDetailsBatch,
  },
  ...injectConditionalArrayItems(timelineAvailable, {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMELINE}`,
    Component: JobDetailsBatchTimeline,
  }),
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}`,
    Component: JobDetailsBatchEventsNavigator,
  },
  // {
  //   name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}`,
  //   Component: JobDetailsBatch,
  // },
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.DOCUMENTS}`,
    Component: JobDetailsBatchDocuments,
  },
];

export const jobDetailsNCFileConfig = [
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.GENERAL}`,
    Component: JobDetailsNCFile,
  },
  // Based on task SF-4963
  // ...injectConditionalArrayItems(timelineAvailable, {
  //   name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMELINE}`,
  //   Component: JobDetailsNCFileTimeline,
  // }),

  // {
  //   name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}`,
  //   Component: JobDetailsNCFileTimes,
  // },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}`,
    Component: JobDetailsNCFileEventsNavigator,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}`,
    Component: JobDetailsNCFilePerformanceNavigator,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.VERSIONS}`,
    Component: JobDetailsNCFileVersions,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.LINKS}`,
    Component: JobDetailsNCFileLinks,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.DOCUMENTS}`,
    Component: JobDetailsNCFileDocuments,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TRACEABILITY}`,
    Component: JobDetailsNCFileTraceability,
  }
];

export const jobDetailsJobsConfig = [
  {
    name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.GENERAL}`,
    Component: JobDetailsJobs,
  },
  ...injectConditionalArrayItems(timelineAvailable, {
    name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.TIMELINE}`,
    Component: JobDetailsJobsTimeline,
  }),
  // {
  //   name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.TIMES}`,
  //   Component: JobDetailsJobs,
  // },
  // {
  //   name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.PERFORMANCE}`,
  //   Component: JobDetailsJobs,
  // },
  // {
  //   name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.VERSIONS}`,
  //   Component: JobDetailsJobs,
  // },
  // {
  //   name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.LINKS}`,
  //   Component: JobDetailsJobs,
  // },
  {
    name: `${JobDetailsScreen.JOBS}${JobLevelSubScreen.DOCUMENTS}`,
    Component: JobDetailsJobsDocuments,
  },
];

export const ncFilePerformanceConfig = [
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${MachinesScreen.ACTIVE}`,
    Component: JobDetailsNCFilePerformanceActive,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${MachinesScreen.PRODUCTIVE}`,
    Component: JobDetailsNCFilePerformanceProductive,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${MachinesScreen.PAID}`,
    Component: JobDetailsNCFilePerformancePaid,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${MachinesScreen.UTILIZATION}`,
    Component: JobDetailsNCFilePerformanceUtilization,
  },
];

export const ncFileEventsConfig = [
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.CYCLE}`,
    Component: JobDetailsNCFileEventsCycle,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.ACTIVE}`,
    Component: JobDetailsNCFileEventsActive,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.CHANGE}`,
    Component: JobDetailsNCFileEventsChange,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.SETUP}`,
    Component: JobDetailsNCFileEventsSetup,
  },
  {
    name: `${JobDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.TRACE}`,
    Component: JobDetailsNCFileEventsTrace,
  },
];

export const batchEventsConfig = [
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.CYCLE}`,
    Component: JobDetailsBatchEventsCycle,
  },
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.ACTIVE}`,
    Component: JobDetailsBatchEventsActive,
  },
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.CHANGE}`,
    Component: JobDetailsBatchEventsChange,
  },
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.SETUP}`,
    Component: JobDetailsBatchEventsSetup,
  },
  {
    name: `${JobDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.TRACE}`,
    Component: JobDetailsBatchEventsTrace,
  },
];
